import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';

/**
 * Company landing page for A and A Enterprises LLC.
 * Highlights software development; business name and contact remain visible for verification.
 * Share /stripe-verification with Stripe when they ask for a URL to verify your business.
 */
const StripeVerification = () => {
  const businessName = 'A and A Enterprises LLC';
  const contactPhone = '(405) 938-5988';
  const contactPhoneRaw = '4059385988';
  const team = [
    { name: 'Aidan Doolen', email: 'aidan@nurturebuddy.ai' },
    { name: 'Aaron Chandra', email: 'aaron@nurturebuddy.ai' },
  ];

  const highlights = [
    {
      icon: 'Code2',
      title: 'Built to last',
      text: 'We build software with maintainable architecture and clear patterns—so it scales with you.',
    },
    {
      icon: 'Layers',
      title: 'Modern stack',
      text: 'From APIs and databases to frontends and automation, we use proven tools and best practices.',
    },
    {
      icon: 'Zap',
      title: 'Ship and iterate',
      text: 'We focus on delivery: working software, clear communication, and iterations that add real value.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>{businessName} — We build software</title>
        <meta name="description" content={`${businessName} builds reliable, modern software. Custom applications, APIs, and automation. Contact us: ${contactPhone}.`} />
      </Helmet>

      <div className="min-h-screen relative flex flex-col overflow-hidden">
        {/* Dark gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950" />

        {/* Subtle grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,.08) 1px, transparent 1px)
            `,
            backgroundSize: '64px 64px',
          }}
        />

        {/* Soft glow orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl" />

        <div className="relative z-10 flex flex-col min-h-screen">
          {/* Hero */}
          <section className="flex-1 flex flex-col items-center justify-center px-6 py-16 sm:py-24">
            <motion.div
              className="w-full max-w-2xl mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-slate-400 text-xs font-medium tracking-wide mb-6">
                <Icon name="Building2" size={14} />
                {businessName}
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight mb-4">
                We build software
              </h1>
              <p className="text-lg sm:text-xl text-slate-400 max-w-xl mx-auto leading-relaxed">
                Reliable applications, clean APIs, and automation that fits your business. We focus on what ships—and what scales.
              </p>
            </motion.div>
          </section>

          {/* How we build */}
          <section className="px-6 pb-16 sm:pb-24">
            <motion.div
              className="w-full max-w-4xl mx-auto"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-widest text-center mb-8">
                How we work
              </h2>
              <div className="grid sm:grid-cols-3 gap-6">
                {highlights.map((item, i) => (
                  <motion.div
                    key={item.title}
                    className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-6"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ delay: i * 0.1, duration: 0.4 }}
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 border border-blue-400/20 flex items-center justify-center mb-4">
                      <Icon name={item.icon} size={20} className="text-blue-400" />
                    </div>
                    <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{item.text}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </section>

          {/* Contact */}
          <section className="px-6 pb-16">
            <motion.div
              className="w-full max-w-xl mx-auto rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 sm:p-10 text-center"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-white font-semibold text-lg mb-2">Get in touch</h2>
              <p className="text-slate-400 text-sm mb-6">
                Have a project in mind? We’d love to hear from you.
              </p>
              <a
                href={`tel:+1${contactPhoneRaw}`}
                className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-colors"
              >
                <Icon name="Phone" size={18} />
                <span className="font-mono tracking-wide">{contactPhone}</span>
              </a>
              <div className="mt-6 space-y-2">
                {team.map((person) => (
                  <p key={person.email} className="text-slate-400 text-sm">
                    <span className="text-white font-medium">{person.name}</span>
                    {' · '}
                    <a
                      href={`mailto:${person.email}`}
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      {person.email}
                    </a>
                  </p>
                ))}
              </div>
              <p className="text-slate-500 text-xs mt-6">
                {businessName}
              </p>
            </motion.div>
          </section>

          {/* Subtle footer - verification not prominent */}
          <footer className="py-6 text-center">
            <p className="text-slate-600 text-xs">
              Verified business · Secure payments accepted
            </p>
          </footer>
        </div>
      </div>
    </>
  );
};

export default StripeVerification;
